/**
 * PillTabs (KAN-734)
 *
 * Row of pill-shaped tabs used by the dashboard cards (attention channel and
 * view tabs). One look for every tab row of the block.
 */
export interface PillTab<T extends string> {
  value: T
  label: string
}

interface PillTabsProps<T extends string> {
  /** Accessible name of the tab list. */
  label: string
  tabs: readonly PillTab<T>[]
  value: T
  onChange: (value: T) => void
  /** Prefix of `data-testid` of each tab: `${testIdPrefix}-${value}`. */
  testIdPrefix?: string
}

export function PillTabs<T extends string>({
  label,
  tabs,
  value,
  onChange,
  testIdPrefix,
}: PillTabsProps<T>) {
  return (
    <div role="tablist" aria-label={label} className="flex flex-wrap gap-2">
      {tabs.map((tab) => {
        const selected = tab.value === value
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={selected}
            data-testid={testIdPrefix ? `${testIdPrefix}-${tab.value}` : undefined}
            onClick={() => onChange(tab.value)}
            className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${
              selected
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}
